import './BottomHeader.scss';

import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ROUTE_ECATALOGS,
  ROUTE_NODES_CLEARANCE,
  ROUTE_NODES_DISCOUNT,
  ROUTE_NODES_EXCLUSIVITY,
  ROUTE_WISHLISTS,
} from '@/common/constants';
import useTheme from '@/common/hooks/useTheme';
import { ReactComponent as FavoriteSvg } from '@/ebat/assets/icons/favorite.svg';
import HeaderNavLink from '@/ebat/components/blocks/Header/HeaderNavLink/HeaderNavLink';
import NotificationsButton from '@/ebat/components/blocks/Header/Notifications/NotificationsButton/NotificationsButton';
import SearchBar from '@/ebat/components/blocks/Header/SearchBar/SearchBar';
import { useHeaderAccessesContext } from '@/ebat/contexts/Header/HeaderAccessesContext/HeaderAccessesContext';
import {
  MENU_TYPE_DRAWER,
  useHeaderMenuContext,
} from '@/ebat/contexts/Header/HeaderMenuContext/HeaderMenuContext';

import {
  BottomHeaderDefaultProps,
  BottomHeaderPropTypes,
} from './BottomHeader.proptypes';

const BottomHeader = ({ className, desktopItemClassName, showSearchBar }) => {
  const { t } = useTranslation();
  const { isOpen, handleClose } = useHeaderMenuContext();
  const {
    partnerUnified,
    partner: hasPartnerAccesses,
    wishlists: hasWishlistsAccesses,
    showClearance,
    showECatalog,
    showPromo,
  } = useHeaderAccessesContext();
  const {
    PartnerLogo,
    partnerHost,
    PartnerUnifiedLogo,
    partnerUnifiedHost,
  } = useTheme();

  return (
    <div
      role="presentation"
      className={cn(className, 'bottom-header', {
        'bottom-header--drawer-open': isOpen({ type: MENU_TYPE_DRAWER }),
      })}
      onClick={handleClose}
    >
      {showPromo && (
        <HeaderNavLink
          className={cn('bottom-header__item', desktopItemClassName)}
          context="bottom-header-item-discount"
          label={t('common:entities:layout.header.discount.label')}
          to={ROUTE_NODES_DISCOUNT}
          variant="secondary"
        />
      )}
      {showClearance && (
        <HeaderNavLink
          className={cn('bottom-header__item', desktopItemClassName)}
          context="bottom-header-item-clearance"
          label={t('ebat:entities:layout.header.destock')}
          to={ROUTE_NODES_CLEARANCE}
          variant="secondary"
        />
      )}
      <HeaderNavLink
        className={cn('bottom-header__item', desktopItemClassName)}
        context="bottom-header-item-exclusivity"
        label={t('ebat:entities:layout.header.exclusivity')}
        to={ROUTE_NODES_EXCLUSIVITY}
        variant="secondary"
      />
      {showECatalog && (
        <HeaderNavLink
          className={cn('bottom-header__item', desktopItemClassName)}
          context="bottom-header-item-ecatalog"
          label={t('ebat:entities:cms.ecatalogs.title')}
          to={ROUTE_ECATALOGS}
          variant="secondary"
        />
      )}
      {hasPartnerAccesses && !partnerUnified && (
        <a
          className={cn('bottom-header__item', 'bottom-header__item-partner')}
          data-qa-id="bottom-header-item-partner"
          href={partnerHost}
        >
          <span className="bottom-header__item-partner-label">
            {t('ebat:entities:layout.header.partner_access')}
          </span>
          <PartnerLogo className="bottom-header__item-partner-logo" />
        </a>
      )}
      {partnerUnified && (
        <a
          className={cn('bottom-header__item', 'bottom-header__item-partner')}
          href={partnerUnifiedHost}
        >
          <span className="bottom-header__item-partner-label">
            {t('ebat:entities:layout.header.partner_access')}
          </span>
          <PartnerUnifiedLogo className="bottom-header__item-lm-logo" />
        </a>
      )}
      <div className={cn('bottom-header__divider', desktopItemClassName)} />
      <HeaderNavLink
        className={cn('bottom-header__item', desktopItemClassName)}
        context={`bottom-header-item-wishlists-${
          !hasWishlistsAccesses ? 'disabled' : 'active'
        }`}
        disabled={!hasWishlistsAccesses}
        Icon={<FavoriteSvg />}
        label={t('ebat:entities:layout.header.my_account.favorite')}
        to={ROUTE_WISHLISTS}
        variant="tertiary"
      />
      <NotificationsButton
        className={cn(
          'bottom-header__item',
          'bottom-header__notification-button',
          desktopItemClassName,
        )}
      />
      <div className="bottom-header__search-container">
        {showSearchBar && <SearchBar />}
      </div>
    </div>
  );
};

BottomHeader.defaultProps = {
  ...BottomHeaderDefaultProps,
};

BottomHeader.propTypes = {
  ...BottomHeaderPropTypes,
};

export default BottomHeader;
